import { unwrapConnection } from "@utils/utils";
import { useMemo } from "react";

import { ReportGoalType, ReportTargetType, useGetReassessmentReportGoalsQuery } from "@api/graphql/types-and-hooks";
import { isFieldContentEmpty } from "@components/FieldComponent/utils";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";

export interface IncompleteNarrativeField {
  id: string;
  label: string;
  sectionTitle: string;
}

interface ReassessmentApprovalProgress {
  approvedGoals: number;
  totalGoals: number;
  approvedTargets: number;
  totalTargets: number;
  completedNarrativeFields: number;
  totalNarrativeFields: number;
  incompleteNarrativeFields: IncompleteNarrativeField[];
  percentage: number;
  isAllApproved: boolean;
}

/**
 * Computes the overall Reassessment report progress: goal approvals, target
 * approvals, and required-narrative-field completion combined into a single
 * percentage. Each item is one unit toward the denominator.
 *
 * Shared by the step-progress wayfinding header (gates Step 1 → Step 2 on
 * full goal approval; the progress chip in the header consumes the full
 * breakdown).
 */
export const useReassessmentApprovalProgress = (reportId: string | undefined): ReassessmentApprovalProgress => {
  const { reportSections } = useClinicalReportDetailsData();
  const reassessmentGoalsQuery = useGetReassessmentReportGoalsQuery(
    { filter: { reportId: reportId ?? "" } },
    {
      queryKey: ["get-reassessment-goals", reportId],
      enabled: !!reportId,
    },
  );

  const goalList: ReportGoalType[] = useMemo(
    () => (unwrapConnection(reassessmentGoalsQuery.data?.getReassessmentReportGoals) as ReportGoalType[]) || [],
    [reassessmentGoalsQuery.data],
  );

  return useMemo(() => {
    const totalGoals = goalList.length;
    const approvedGoals = goalList.filter((g) => g.summaryReviewed).length;

    let totalTargets = 0;
    let approvedTargets = 0;
    for (const goal of goalList) {
      const targets = (unwrapConnection(goal.reporttargetSet) as ReportTargetType[]) || [];
      const includedTargets = targets.filter((t) => !t.excludeFromReport);
      totalTargets += includedTargets.length;
      approvedTargets += includedTargets.filter((t) => t.summaryReviewed).length;
    }

    // Scope to sections that actually render on the Reassessment Narrative
    // tab. Sections tagged for the Goal Progress step (`treatment-plan`)
    // live on a different URL and use goals/targets, not section fields —
    // counting required fields from there would gate Narrative → Review on
    // content the clinician can't reach from the Narrative tab. All other
    // pageLocation values (report-part-1/2, recommendations, conclusions,
    // appendix) render on Narrative + Review.
    const narrativeSections = reportSections.filter((s) => (s.pageLocation ?? "") !== "treatment-plan");
    const requiredFields = narrativeSections.flatMap((s) =>
      (s.sectionField?.edges ?? [])
        .map((e) => e?.node)
        .filter((f) => !!f?.templateSectionField?.isRequired)
        .map((f) => ({ node: f!, sectionTitle: s.title ?? "" })),
    );
    const totalNarrativeFields = requiredFields.length;
    const incompleteNarrativeFields: IncompleteNarrativeField[] = requiredFields
      .filter(({ node }) => isFieldContentEmpty(node.fieldType, node.content))
      .map(({ node, sectionTitle }) => ({ id: node.id, label: node.title ?? "Untitled field", sectionTitle }));
    const completedNarrativeFields = totalNarrativeFields - incompleteNarrativeFields.length;

    const totalItems = totalGoals + totalTargets + totalNarrativeFields;
    const approvedItems = approvedGoals + approvedTargets + completedNarrativeFields;
    const percentage = totalItems > 0 ? Math.floor((approvedItems / totalItems) * 100) : 0;

    return {
      approvedGoals,
      totalGoals,
      approvedTargets,
      totalTargets,
      completedNarrativeFields,
      totalNarrativeFields,
      incompleteNarrativeFields,
      percentage,
      isAllApproved: totalItems > 0 && approvedItems === totalItems,
    };
  }, [goalList, reportSections]);
};
